
import { defineComponent, onMounted, ref } from "vue";
import { ErrorMessage, Field, Form } from "vee-validate";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import Swal from "sweetalert2/dist/sweetalert2.js";
import * as Yup from "yup";
import mAxiosApi from "@/api";

import store from "@/store";
import { Mutations } from "@/store/enums/StoreEnums";

interface ProfileDetails {
  uti_c_prenom: string;
  uti_c_nom: string;
  uti_c_phone: string;
  uti_c_service: string;
  uti_c_mail: string;
  uti_c_newmail: string;
  uti_c_password: string;
  uti_c_password_new: string;
  uti_c_password_new_confirm: string;
}

export default defineComponent({
  name: "account-settings",
  components: {
    ErrorMessage,
    Field,
    Form,
  },
  setup() {
    const submitButton1 = ref<HTMLElement | null>(null);
    const updateEmailButton = ref<HTMLElement | null>(null);
    const updatePasswordButton = ref<HTMLElement | null>(null);

    const emailFormDisplay = ref(false);
    const passwordFormDisplay = ref(false);

    const profileDetailsValidator = Yup.object().shape({
      uti_c_prenom: Yup.string().nullable().required("Le prénom est obligatoire"),
      uti_c_nom: Yup.string().nullable().required("Le nom est obligatoire"),
      uti_c_phone: Yup.string().nullable().label("Télephone Fixe"),
      uti_c_service: Yup.string().nullable().label("CCO ID"),
    });

    const changeEmail = Yup.object().shape({
      emailaddress: Yup.string().required().email().label("Email"),
      confirmemailpassword: Yup.string().required().label("Password"),
    });

    const changePassword = Yup.object().shape({
      currentpassword: Yup.string()
        .required("Mot de passe actuel obligatoire")
        .label("Current password"),
      newpassword: Yup.string()
        .min(8)
        .required("Mot de passe actuel obligatoire")
        .label("Password"),
      confirmpassword: Yup.string()
        .min(8)
        .required("Confirmation du nouveau Mot de passe obligatoire")
        .oneOf(
          [Yup.ref("newpassword"), null],
          "Les nouveaux mots des passes doivent être identiques"
        )
        .label("Password Confirmation"),
    });

    const profileDetails = ref<ProfileDetails>({
      uti_c_prenom: "",
      uti_c_nom: "",
      uti_c_phone: "",
      uti_c_service: "",
      uti_c_mail: "",
      uti_c_newmail: "",
      uti_c_password: "",
      uti_c_password_new: "",
      uti_c_password_new_confirm: "",
    });

    const saveChanges1 = () => {
      if (submitButton1.value) {
        // Activate indicator
        submitButton1.value.setAttribute("data-kt-indicator", "on");

        mAxiosApi
          .post("updateProfil", profileDetails)
          .then(({ data }) => {
            // console.log(data["results"]);
            submitButton1.value?.removeAttribute("data-kt-indicator");

            store.commit(Mutations.SET_USER, data["results"]);

            Swal.fire({
              text: "Changement de vos informations personnelles correctement effectué !",
              icon: "success",
              confirmButtonText: "Ok",
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-light-primary",
              },
            });
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };

    const updateEmail = () => {
      if (updateEmailButton.value) {
        // Activate indicator
        updateEmailButton.value.setAttribute("data-kt-indicator", "on");

        mAxiosApi
          .post("updateMail", profileDetails)
          .then(({ data }) => {
            // console.log(data);
            updateEmailButton.value?.removeAttribute("data-kt-indicator");

            if (data["results"] !== 0) {
              store.commit(Mutations.SET_USER, data["results"]);
              emailFormDisplay.value = false;
              profileDetails.value.uti_c_mail = profileDetails.value.uti_c_newmail;
              Swal.fire({
                text: "Changement de votre Email correctement effectué !",
                icon: "success",
                confirmButtonText: "Ok",
                buttonsStyling: false,
                customClass: {
                  confirmButton: "btn btn-light-primary",
                },
              });
            } else {
              Swal.fire({
                text: "Erreur lors du chagement de mail. Veuillez vérifier votre adresse Email et votre mot de passe, svp.",
                icon: "error",
                confirmButtonText: "Ok",
                buttonsStyling: false,
                customClass: {
                  confirmButton: "btn btn-light-primary",
                },
              });
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };

    const updatePassword = () => {
      if (updatePasswordButton.value) {
        // Activate indicator
        updatePasswordButton.value.setAttribute("data-kt-indicator", "on");

        mAxiosApi
          .post("password", {
            username: profileDetails.value.uti_c_mail,
            password: profileDetails.value.uti_c_password,
            newPassword: profileDetails.value.uti_c_password_new,
          })
          .then(({ data }) => {
            // console.log(data);
            updatePasswordButton.value?.removeAttribute("data-kt-indicator");
            passwordFormDisplay.value = false;
            Swal.fire({
              text: "Changement de votre mot de passe correctement effectué !",
              icon: "success",
              confirmButtonText: "Ok",
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-light-primary",
              },
            });
          })
          .catch(() => {
            updatePasswordButton.value?.removeAttribute("data-kt-indicator");
            Swal.fire({
              text: "Erreur lors du chagement de mot de passe. Veuillez vérifier vos informations et réessayer, svp.",
              icon: "error",
              confirmButtonText: "Ok",
              buttonsStyling: false,
              customClass: {
                confirmButton: "btn btn-light-primary",
              },
            });
          });
      }
    };

    onMounted(async () => {
      setCurrentPageBreadcrumbs("Paramètres du compte", [""]);
      let meAndMyself = await getAxios("/me");
      // console.log(meAndMyself);

      profileDetails.value.uti_c_prenom = meAndMyself.uti_c_prenom;
      profileDetails.value.uti_c_nom = meAndMyself.uti_c_nom;
      profileDetails.value.uti_c_phone = meAndMyself.uti_c_phone;
      profileDetails.value.uti_c_service = meAndMyself.uti_c_service;
      profileDetails.value.uti_c_mail = meAndMyself.uti_c_mail;
      profileDetails.value.uti_c_newmail = meAndMyself.uti_c_mail;
      profileDetails.value.uti_c_password = "";
      profileDetails.value.uti_c_password_new = "";
      profileDetails.value.uti_c_password_new_confirm = "";
    });

    return {
      submitButton1,
      saveChanges1,
      profileDetails,
      emailFormDisplay,
      passwordFormDisplay,
      profileDetailsValidator,
      changeEmail,
      changePassword,
      updateEmailButton,
      updatePasswordButton,
      updateEmail,
      updatePassword,
    };
  },
});
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function getAxios(request: string): Promise<any> {
  const response = await mAxiosApi.get(request);
  return response.data;
}
